/**
 * The guest's free-text note on ONE order line ("no onion on one, no pickles on both").
 *
 * Contract shared with iOS, Android and the backend (ai-work/item-notes/item-notes.md):
 *  - carried as a line-item `meta_data` entry under exactly this key, for WooCommerce and Hype alike;
 *  - plain text, trimmed, at most `ITEM_NOTE_MAX_LENGTH` characters; empty means "no note";
 *  - NOT part of the line key — `cartLineKey` must never read it. Two units of one dish with one
 *    note are one line, and how the note maps onto the units is the guest's business.
 *
 * Pure, like `supplement-pricing.ts`: no Angular, no storage.
 */
export const ITEM_NOTE_META_KEY = 'item_note';

export const ITEM_NOTE_MAX_LENGTH = 200;

/**
 * A note as it may be stored and sent: trimmed, control characters dropped (newlines included —
 * the backend strips them too, and a kitchen ticket line is one line), capped at the limit.
 *
 * Capped by code point rather than UTF-16 unit, so an emoji at the boundary is dropped whole
 * instead of leaving half a surrogate pair behind.
 */
export function normalizeItemNote(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const cleaned = raw.replace(/[\u0000-\u001F\u007F]+/g, ' ').trim();
  return Array.from(cleaned).slice(0, ITEM_NOTE_MAX_LENGTH).join('').trim();
}

/**
 * The note on a SERVER line's `meta_data`, or `''`.
 *
 * Exact key match, like every other meta lookup in this app — a prefix match would let some
 * future `item_note_*` key masquerade as the note.
 */
export function itemNoteFromMeta(metaData: unknown): string {
  if (!Array.isArray(metaData)) return '';
  const entry = metaData.find(meta => meta?.key === ITEM_NOTE_META_KEY);
  return normalizeItemNote(entry?.value);
}

/** One side of an add-on edit that collapses two lines into one. */
export interface MergingNoteLine {
  note?: string;
  /** The line's quantity at merge time — what its note was written about. */
  quantity: number;
}

/**
 * The note for a line produced by merging two lines (an add-on edit moved one onto a key the cart
 * already held): each source note prefixed with its own line's quantity, the existing (target)
 * line first, then the moved one — `"2x no onion; 1x well done"`.
 *
 * A note is prefixed even when it is the only one, so it does not read as applying to the whole
 * merged quantity. Neither line noted → no note (`''`). Capped like any other note.
 */
export function mergeItemNotes(target: MergingNoteLine, moved: MergingNoteLine): string {
  const parts = [target, moved]
    .map(line => ({ note: normalizeItemNote(line.note), quantity: Math.max(0, Math.trunc(line.quantity)) }))
    .filter(line => line.note !== '')
    .map(line => `${line.quantity}x ${line.note}`);
  return normalizeItemNote(parts.join('; '));
}
